import { createContext, useCallback, useMemo, useState } from 'react';
import { authApi } from '../api';

const CURRENT_USER_STORAGE_KEY = 'obank_current_user';

function readStoredCurrentUser() {
  const raw = sessionStorage.getItem(CURRENT_USER_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistCurrentUser(currentUser) {
  if (!currentUser) {
    sessionStorage.removeItem(CURRENT_USER_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(CURRENT_USER_STORAGE_KEY, JSON.stringify(currentUser));
}

export const CurrentUserContext = createContext({
  currentUser: null,
  isCurrentUserLoading: false,
  refreshCurrentUser: () => Promise.resolve(null),
  setCurrentUser: () => {},
});

export function CurrentUserProvider({ children, authService = authApi }) {
  const [currentUser, setCurrentUserState] = useState(readStoredCurrentUser);
  const [isCurrentUserLoading, setIsCurrentUserLoading] = useState(false);

  const setCurrentUser = useCallback((nextUserOrUpdater) => {
    setCurrentUserState((prevUser) => {
      const nextUser = typeof nextUserOrUpdater === 'function'
        ? nextUserOrUpdater(prevUser)
        : nextUserOrUpdater;

      persistCurrentUser(nextUser);
      return nextUser;
    });
  }, []);

  const refreshCurrentUser = useCallback(async (options = {}) => {
    const { silent = false } = options;

    if (!silent) {
      setIsCurrentUserLoading(true);
    }

    try {
      const nextCurrentUser = await authService.getMe();
      setCurrentUser(nextCurrentUser);
      return nextCurrentUser;
    } catch (error) {
      if (error?.statusCode === 401) {
        setCurrentUser(null);
      }

      return null;
    } finally {
      if (!silent) {
        setIsCurrentUserLoading(false);
      }
    }
  }, [authService, setCurrentUser]);

  const value = useMemo(
    () => ({
      currentUser,
      isCurrentUserLoading,
      refreshCurrentUser,
      setCurrentUser,
    }),
    [currentUser, isCurrentUserLoading, refreshCurrentUser, setCurrentUser]
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}
